// lib/format.ts — единые хелперы форматирования дат (date-fns + ru locale)
// и локализации enum-значений через i18n (R41 / U-16: конвенция <domain>.<field>.<VALUE>).

import { format, parse } from 'date-fns';
import { ru } from 'date-fns/locale';

/** Placeholder для отсутствующего значения. */
const EMPTY = '—';

/** Функция перевода (канон `t` из '@/i18n'; передаётся параметром для тестируемости). */
export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Локализованная подпись enum-значения.
 * <p>
 * Пытается достать ключ `<prefix>.<VALUE>`; если ключа нет в словаре —
 * возвращает сырое значение `rawFallback ?? valueKey` (не список ключ-строку) —
 * расширение бэкенда не ломает UI (fallback-контракт: «нет ключа → как есть»).
 */
function enumLabel(
  valueKey: string | null | undefined,
  t: TranslateFn,
  prefix: string,
  rawFallback?: string,
): string {
  if (!valueKey) return EMPTY;
  const key = `${prefix}.${valueKey}`;
  const label = t(key);
  return label === key ? (rawFallback ?? valueKey) : label;
}

/** Форматирует ISO datetime как «d MMM yyyy, HH:mm» (ru). Null/undefined → «—». */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  return format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: ru });
}

/** Форматирует backend LocalDate «YYYY-MM-DD» как «d MMM yyyy» (ru). Null/undefined → «—». */
export function formatDateOnly(dateStr: string | null | undefined): string {
  if (!dateStr) return EMPTY;
  // parse вместо new Date: локальная полночь без сдвига дня по часовому поясу.
  return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: ru });
}

/** Локализованная подпись стадии сделки (R41 / U-16; ключи `crm.deals.stage.*`). */
export function formatStage(stage: string | null | undefined, t: TranslateFn): string {
  return enumLabel(stage, t, 'crm.deals.stage');
}

/** Локализованная подпись статуса задачи (R41 / U-16; ключи `tasks.status.*`). */
export function formatTaskStatus(status: string | null | undefined, t: TranslateFn): string {
  return enumLabel(status, t, 'tasks.status');
}

/** Локализованная подпись статуса лида (V12 U-34; ключи `leads.status.*`). */
export function formatLeadStatus(status: string | null | undefined, t: TranslateFn): string {
  return enumLabel(status, t, 'leads.status');
}

/** Локализованная подпись источника лида (V12 U-34; ключи `leads.source.*`). */
export function formatLeadSource(source: string | null | undefined, t: TranslateFn): string {
  return enumLabel(source, t, 'leads.source');
}

/** Локализованная подпись статуса платежа (V12 U-35; ключи `payments.status.*`). */
export function formatPaymentStatus(status: string | null | undefined, t: TranslateFn): string {
  return enumLabel(status, t, 'payments.status');
}

/** Локализованная подпись источника платежа (V12 U-35; ключи `payments.source.*`). */
export function formatPaymentSource(source: string | null | undefined, t: TranslateFn): string {
  return enumLabel(source, t, 'payments.source');
}

/** Локализованная подпись приоритета задачи (R41 / U-16; ключи `tasks.priority.*`). */
export function formatTaskPriority(priority: string | null | undefined, t: TranslateFn): string {
  return enumLabel(priority, t, 'tasks.priority');
}

/** Локализованная подпись типа события календаря (R41 / U-16; ключи `calendar.type.*`). */
export function formatEventType(type: string | null | undefined, t: TranslateFn): string {
  return enumLabel(type, t, 'calendar.type');
}

/** Локализованная подпись статуса участника события (R41 / U-16; ключи `calendar.attendee.*`). */
export function formatAttendeeStatus(status: string | null | undefined, t: TranslateFn): string {
  return enumLabel(status, t, 'calendar.attendee');
}

/** Локализованная подпись типа события аудита (R41 / U-16; ключи `audit.eventType.*`). */
export function formatAuditType(eventType: string | null | undefined, t: TranslateFn): string {
  return enumLabel(eventType, t, 'audit.eventType');
}

/**
 * Локализованная подпись типа сущности аудита (R41 / U-16; ключи `audit.entity.*`).
 * На проводе entityType — lowercase («task», «deal»), ключ строится из UPPER;
 * fallback (нет ключа) — исходный lowercase.
 */
export function formatAuditEntity(entityType: string | null | undefined, t: TranslateFn): string {
  return enumLabel(entityType?.toUpperCase(), t, 'audit.entity', entityType ?? undefined);
}
